// VFX Config Context
// React Context to manage VFX config state and polling logic

import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { vfxConfigService } from '../services/vfxConfigService';
import type { VFXConfigState } from '../types/vfxConfig';
import SplashScreen from '@/components/SplashScreen';

interface VFXConfigContextValue {
  state: VFXConfigState;
  refreshConfig: () => Promise<void>;
}

const VFXConfigContext = createContext<VFXConfigContextValue | undefined>(undefined);

interface VFXConfigProviderProps {
  children: ReactNode;
}

export function VFXConfigProvider({ children }: VFXConfigProviderProps) {
  const [state, setState] = useState<VFXConfigState>({
    config: null,
    isLoading: true,
    error: null,
    lastFetched: null,
    etag: null,
  });

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const isMountedRef = useRef(true);
  const consecutiveFailuresRef = useRef(0);
  const MAX_CONSECUTIVE_FAILURES = 10;

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const handleFailure = useCallback((errorMessage: string) => {
    consecutiveFailuresRef.current += 1;
    if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
      stopPolling();
    }
    setState((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
  }, [stopPolling]);

  const applyResult = useCallback((result: Awaited<ReturnType<typeof vfxConfigService.fetchConfig>>) => {
    if (!result.success) {
      handleFailure(result.error || 'Failed to fetch VFX config');
      return;
    }
    consecutiveFailuresRef.current = 0;
    if (result.notModified) {
      setState((prev) => ({ ...prev, isLoading: false, lastFetched: Date.now() }));
    } else if (result.config) {
      setState({ config: result.config, isLoading: false, error: null, lastFetched: Date.now(), etag: result.etag });
    } else {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [handleFailure]);

  const fetchConfig = useCallback(async (isInitial = false) => {
    if (isPollingRef.current && !isInitial) return;
    isPollingRef.current = true;
    try {
      if (isInitial) setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const result = await vfxConfigService.fetchConfig();
      if (!isMountedRef.current) return;
      applyResult(result);
    } catch (error) {
      if (!isMountedRef.current) return;
      handleFailure(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      isPollingRef.current = false;
    }
  }, [applyResult, handleFailure]);

  // Manual refresh function
  const refreshConfig = useCallback(async () => {
    await fetchConfig(false);
  }, [fetchConfig]);

  // Initial fetch on mount
  useEffect(() => {
    isMountedRef.current = true;
    fetchConfig(true);

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchConfig]);

  // Set up polling interval
  useEffect(() => {
    const interval = vfxConfigService.getPollingInterval();

    // Start polling after initial fetch
    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(() => {
        if (isMountedRef.current && !isPollingRef.current) {
          fetchConfig(false);
        }
      }, interval);
    };

    // Start polling after a short delay to allow initial fetch to complete
    const timeoutId = setTimeout(startPolling, 1000);

    return () => {
      clearTimeout(timeoutId);
      stopPolling();
    };
  }, [fetchConfig, stopPolling]);

  // Refetch when tab becomes visible so changes apply after 60s or when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isMountedRef.current && !isPollingRef.current) {
        // Reset failure counter on tab focus so polling can resume if it was stopped
        consecutiveFailuresRef.current = 0;
        fetchConfig(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchConfig]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  // Track whether initial config has been loaded (first fetch completed)
  const [initialLoaded, setInitialLoaded] = useState(false);
  // Track whether splash screen has finished its fade-out animation
  const [splashFinished, setSplashFinished] = useState(false);

  // Mark initial load complete when isLoading transitions to false for the first time
  useEffect(() => {
    if (!state.isLoading && !initialLoaded) {
      setInitialLoaded(true);
    }
  }, [state.isLoading, initialLoaded]);

  const handleSplashFinished = useCallback(() => {
    setSplashFinished(true);
  }, []);

  const value: VFXConfigContextValue = {
    state,
    refreshConfig,
  };

  return (
    <VFXConfigContext.Provider value={value}>
      {/* Splash screen: visible until config is loaded + fade-out completes */}
      {!splashFinished && (
        <SplashScreen
          isReady={initialLoaded}
          onFinished={handleSplashFinished}
        />
      )}
      {/* Render children only after initial config is loaded (prevents theme flash) */}
      {initialLoaded && children}
    </VFXConfigContext.Provider>
  );
}

/**
 * Hook to use VFX config context
 * @throws Error if used outside VFXConfigProvider
 */
export function useVFXConfigContext(): VFXConfigContextValue {
  const context = useContext(VFXConfigContext);
  if (context === undefined) {
    throw new Error('useVFXConfigContext must be used within VFXConfigProvider');
  }
  return context;
}

